import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface Star {
  x: number;
  y: number;
  radius: number;
  alpha: number;
  alphaDir: number;
  pulseSpeed: number;
  maxAlpha: number;
  twinkle: boolean;
}

const orbitIcons = [
  { icon: 'ri-car-line',          color: 'rgba(255,255,255,0.35)', bg: 'rgba(255,255,255,0.05)',  border: 'rgba(255,255,255,0.12)' },
  { icon: 'ri-steering-2-line',   color: 'rgba(255,255,255,0.35)', bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.12)' },
  { icon: 'ri-shield-check-line', color: 'rgba(255,255,255,0.35)', bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.12)' },
  { icon: 'ri-map-pin-line',      color: 'rgba(255,255,255,0.35)', bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.12)' },
  { icon: 'ri-user-3-line',       color: 'rgba(255,255,255,0.35)', bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.12)' },
  { icon: 'ri-wallet-2-line',     color: 'rgba(255,255,255,0.35)', bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.12)' },
  { icon: 'ri-star-line',         color: 'rgba(255,255,255,0.35)', bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.12)' },
  { icon: 'ri-gift-line',         color: 'rgba(255,255,255,0.35)', bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.12)' },
];

// Tilt angle: how "diagonal" the orbit looks (55° = pronounced 3D depth)
const TILT_DEG = 55;
const TILT_RAD = (TILT_DEG * Math.PI) / 180;
const TILT_COS = Math.cos(TILT_RAD); // ≈ 0.574 — vertical compression ratio

const HeroSection = () => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: -9999, y: -9999 });
  const starsRef = useRef<Star[]>([]);
  const animRef = useRef<number>(0);

  // 3D orbit refs
  const orbitContainerRef = useRef<HTMLDivElement>(null);
  const orbitAngleRef = useRef(0);
  const orbitAnimRef = useRef<number>(0);

  // 3D orbit animation
  useEffect(() => {
    const COUNT = orbitIcons.length;
    // 100s full rotation at ~60fps
    const SPEED = (2 * Math.PI) / (100 * 60);

    const getRadius = () => {
      const vw = window.innerWidth;
      return Math.min(168, Math.max(84, vw * 0.18));
    };

    const animate = () => {
      orbitAngleRef.current += SPEED;
      const container = orbitContainerRef.current;
      if (!container) {
        orbitAnimRef.current = requestAnimationFrame(animate);
        return;
      }

      const icons = container.querySelectorAll<HTMLElement>('.orbit-icon');
      const radius = getRadius();

      icons.forEach((el, i) => {
        const angle = orbitAngleRef.current + (i * 2 * Math.PI) / COUNT;
        const cosA = Math.cos(angle);
        const sinA = Math.sin(angle);

        // 3D projected position
        const px = cosA * radius;
        const py = sinA * TILT_COS * radius;
        const z = sinA * Math.sin(TILT_RAD); // -1 = back, +1 = front

        // Depth-based visual properties
        const depth = (z + 1) / 2; // 0 (back) → 1 (front)
        const scale = (0.52 + 0.52 * depth).toFixed(3);
        const opacity = (0.18 + 0.82 * depth).toFixed(3);
        const zIndex = Math.round(depth * 20);

        el.style.transform = `translate(calc(${px.toFixed(2)}px - 50%), calc(${py.toFixed(2)}px - 50%)) scale(${scale})`;
        el.style.opacity = opacity;
        el.style.zIndex = String(zIndex);
      });

      orbitAnimRef.current = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(orbitAnimRef.current);
  }, []);

  // Stars canvas animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      generateStars();
    };

    const generateStars = () => {
      const count = Math.floor((canvas.width * canvas.height) / 5500);
      starsRef.current = Array.from({ length: count }, () => {
        const isBright = Math.random() > 0.6;
        return {
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: isBright ? Math.random() * 2.4 + 1.0 : Math.random() * 1.2 + 0.4,
          alpha: Math.random() * 0.12 + 0.06,
          alphaDir: Math.random() > 0.5 ? 1 : -1,
          pulseSpeed: isBright ? 0.007 + Math.random() * 0.009 : 0.002 + Math.random() * 0.003,
          maxAlpha: isBright ? 0.65 + Math.random() * 0.32 : 0.22 + Math.random() * 0.12,
          twinkle: isBright,
        };
      });
    };

    const drawStarShape = (x: number, y: number, outerR: number, innerR: number) => {
      ctx.beginPath();
      for (let i = 0; i < 10; i++) {
        const angle = (Math.PI / 5) * i - Math.PI / 2;
        const r = i % 2 === 0 ? outerR : innerR;
        const px = x + Math.cos(angle) * r;
        const py = y + Math.sin(angle) * r;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const stars = starsRef.current;
      const mouse = mouseRef.current;
      const CONNECTION_DIST = 180;

      const nearStars = new Set<number>();
      if (mouse.x > 0) {
        stars.forEach((star, i) => {
          const dx = star.x - mouse.x;
          const dy = star.y - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DIST) nearStars.add(i);
        });
      }

      stars.forEach((star, i) => {
        star.alpha += star.alphaDir * star.pulseSpeed;
        if (star.alpha >= star.maxAlpha) { star.alpha = star.maxAlpha; star.alphaDir = -1; }
        if (star.alpha <= 0.01) { star.alpha = 0.01; star.alphaDir = 1; }

        const isNear = nearStars.has(i);
        const dx = star.x - mouse.x;
        const dy = star.y - mouse.y;
        const dist = isNear ? Math.sqrt(dx * dx + dy * dy) : CONNECTION_DIST;
        const proximity = isNear ? 1 - dist / CONNECTION_DIST : 0;

        if (isNear && proximity > 0.05) {
          const haloR = star.radius * (5 + proximity * 6);
          const haloGrad = ctx.createRadialGradient(star.x, star.y, 0, star.x, star.y, haloR);
          haloGrad.addColorStop(0, `rgba(255, 255, 255, ${proximity * 0.22})`);
          haloGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
          ctx.beginPath();
          ctx.arc(star.x, star.y, haloR, 0, Math.PI * 2);
          ctx.fillStyle = haloGrad;
          ctx.fill();

          const rayLen = star.radius * (3 + proximity * 5);
          ctx.save();
          ctx.strokeStyle = `rgba(255,255,255,${0.4 + proximity * 0.5})`;
          ctx.lineWidth = 0.6;
          ctx.shadowBlur = 6 + proximity * 10;
          ctx.shadowColor = 'rgba(255,255,255,0.9)';
          for (let angle = 0; angle < Math.PI; angle += Math.PI / 4) {
            ctx.beginPath();
            ctx.moveTo(star.x + Math.cos(angle) * rayLen, star.y + Math.sin(angle) * rayLen);
            ctx.lineTo(star.x - Math.cos(angle) * rayLen, star.y - Math.sin(angle) * rayLen);
            ctx.stroke();
          }
          ctx.restore();

          const outerR = star.radius * (1.8 + proximity * 2.2);
          const innerR = outerR * 0.42;
          ctx.save();
          ctx.shadowBlur = 14 + proximity * 22;
          ctx.shadowColor = 'rgba(255, 255, 255, 0.95)';
          ctx.fillStyle = `rgba(255, 255, 255, ${0.8 + proximity * 0.2})`;
          drawStarShape(star.x, star.y, outerR, innerR);
          ctx.fill();
          ctx.restore();
        } else {
          const outerR = star.radius;
          const innerR = outerR * 0.42;
          if (star.twinkle) {
            ctx.save();
            ctx.shadowBlur = 12 + star.alpha * 40;
            ctx.shadowColor = 'rgba(255, 255, 255, 1)';
            ctx.fillStyle = `rgba(255, 255, 255, ${star.alpha})`;
            drawStarShape(star.x, star.y, outerR, innerR);
            ctx.fill();
            if (star.alpha > 0.45) {
              ctx.shadowBlur = 20 + star.alpha * 20;
              ctx.fillStyle = `rgba(255, 255, 255, ${star.alpha * 0.6})`;
              ctx.beginPath();
              ctx.arc(star.x, star.y, outerR * 0.35, 0, Math.PI * 2);
              ctx.fill();
            }
            ctx.restore();
          } else {
            ctx.fillStyle = `rgba(255, 255, 255, ${star.alpha})`;
            drawStarShape(star.x, star.y, outerR, innerR);
            ctx.fill();
          }
        }
      });

      if (mouse.x > 0) {
        const cursorGrad = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, 20);
        cursorGrad.addColorStop(0, 'rgba(255, 255, 255, 0.25)');
        cursorGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, 20, 0, Math.PI * 2);
        ctx.fillStyle = cursorGrad;
        ctx.fill();

        ctx.save();
        ctx.shadowBlur = 18;
        ctx.shadowColor = 'rgba(255, 255, 255, 0.95)';
        ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
        drawStarShape(mouse.x, mouse.y, 5, 2.1);
        ctx.fill();
        ctx.restore();
      }

      animRef.current = requestAnimationFrame(draw);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('resize', resize);
    resize();
    draw();

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animRef.current);
    };
  }, []);

  return (
    <section className="relative w-full min-h-screen bg-[#060D1A] flex items-center justify-center overflow-hidden">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }} />

      {/* Background image */}
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="https://storage.readdy-site.link/project_files/8471bf70-5a6a-4234-ac9f-9bad5a1c4b2b/0c072bd7-149c-4cdc-ba62-a0f131631c03_Gemini_Generated_Image_119tiu119tiu119t.png?v=9bbc3278213a530791e91f728cf9b153"
          alt="UNIVERSO SIG MOBI"
          className="w-full h-full object-cover object-center select-none"
          style={{ opacity: 0.2 }}
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(6,13,26,0.6) 0%, rgba(6,13,26,0.12) 40%, rgba(6,13,26,0.5) 78%, rgba(6,13,26,0.96) 100%)' }} />
      </div>

      <style>{`
        @keyframes logoFloat {
          0%, 100% { transform: translateY(0px); filter: drop-shadow(0 0 22px rgba(0,196,240,0.38)) drop-shadow(0 0 8px rgba(232,54,93,0.15)); }
          50% { transform: translateY(-10px); filter: drop-shadow(0 0 38px rgba(0,196,240,0.58)) drop-shadow(0 0 14px rgba(232,54,93,0.25)); }
        }
        @keyframes ringPulse {
          0%, 100% { opacity: 0.29; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.50; transform: translate(-50%, -50%) scale(1.03); }
        }
        @keyframes ringPulseOuter {
          0%, 100% { opacity: 0.21; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.36; transform: translate(-50%, -50%) scale(1.05); }
        }
        @keyframes heroFadeUp {
          from { opacity: 0; transform: translateY(28px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pillFade {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .hero-title { animation: heroFadeUp 0.9s ease both; animation-delay: 0.1s; }
        .hero-logo { animation: heroFadeUp 0.9s ease both; animation-delay: 0.3s; }
        .hero-desc { animation: heroFadeUp 0.9s ease both; animation-delay: 0.5s; }
        .hero-btns { animation: heroFadeUp 0.9s ease both; animation-delay: 0.65s; }
        .hero-pill { animation: pillFade 0.8s ease both; animation-delay: 0.05s; }
        .orbit-icon { position: absolute; top: 50%; left: 50%; will-change: transform, opacity; }
      `}</style>

      <div className="relative z-10 flex flex-col items-center justify-center text-center px-5 sm:px-8 pt-24 pb-20 w-full">

        {/* Pill */}
        <div className="hero-pill mb-5 flex items-center gap-3">
          <span className="w-8 sm:w-12 h-px" style={{ background: 'rgba(232,54,93,0.55)' }} />
          <span className="text-[#00C4F0] text-[10px] sm:text-xs font-semibold tracking-[0.28em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>{t('home.hero.eyebrow')}</span>
          <span className="w-8 sm:w-12 h-px" style={{ background: 'rgba(232,54,93,0.55)' }} />
        </div>

        {/* UNIVERSO SIG */}
        <h1
          className="hero-title text-white select-none leading-none"
          style={{
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 900,
            fontSize: 'clamp(2.4rem, 10vw, 9rem)',
            letterSpacing: '-0.03em',
          }}
        >
          {t('home.hero.title')}
        </h1>

        {/* Thin rose accent below title */}
        <div className="flex items-center gap-3 mt-3 mb-6">
          <span className="flex-1 max-w-[80px] sm:max-w-[120px] h-px" style={{ background: 'linear-gradient(to right, transparent, rgba(232,54,93,0.55))' }} />
          <span className="w-1.5 h-1.5 rounded-full bg-[#E8365D]" style={{ opacity: 0.8 }} />
          <span className="flex-1 max-w-[80px] sm:max-w-[120px] h-px" style={{ background: 'linear-gradient(to left, transparent, rgba(232,54,93,0.55))' }} />
        </div>

        {/* Logo with 3D orbiting icons */}
        <div className="hero-logo relative flex items-center justify-center mb-7" style={{ width: 'clamp(168px, 36vw, 336px)', height: 'clamp(168px, 36vw, 336px)' }}>

          {/* Inner ring — circle around logo */}
          <div
            className="absolute pointer-events-none rounded-full"
            style={{
              width: 'clamp(119px, 25vw, 238px)',
              height: 'clamp(119px, 25vw, 238px)',
              top: '50%',
              left: '50%',
              border: '1px solid rgba(0,196,240,0.29)',
              animation: 'ringPulse 4s ease-in-out infinite',
            }}
          />

          {/* Outer orbit track — ellipse matching 3D tilt (width × cos(55°) ≈ 57.4%) */}
          <div
            className="absolute pointer-events-none"
            style={{
              width: 'clamp(168px, 36vw, 336px)',
              height: `calc(clamp(168px, 36vw, 336px) * ${TILT_COS.toFixed(4)})`,
              top: '50%',
              left: '50%',
              borderRadius: '50%',
              border: '1px solid rgba(232,54,93,0.21)',
              animation: 'ringPulseOuter 5.5s ease-in-out infinite',
            }}
          />

          {/* Icons container — JS-driven 3D orbit */}
          <div
            ref={orbitContainerRef}
            className="absolute inset-0 pointer-events-none"
          >
            {orbitIcons.map((item, i) => (
              <div key={i} className="orbit-icon">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ background: item.bg, border: `1px solid ${item.border}` }}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${item.icon} text-sm`} style={{ color: item.color }} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Logo image */}
          <img
            src="https://storage.readdy-site.link/project_files/8471bf70-5a6a-4234-ac9f-9bad5a1c4b2b/83ac0fe0-7767-4fd1-b742-a771a87fd226_LOGO-SIG-MOBI-PNG-Fundo-Transparente-1.png?v=a82771fdc6e27836d6f7f323524a524b"
            alt="SIG MOBI Logo"
            className="relative select-none"
            style={{
              zIndex: 10,
              width: 'clamp(120px, 26vw, 230px)',
              height: 'auto',
              filter: 'drop-shadow(0 0 22px rgba(0,196,240,0.38)) drop-shadow(0 0 8px rgba(232,54,93,0.15))',
            }}
          />
        </div>

        {/* Description */}
        <p
          className="hero-desc max-w-xs sm:max-w-sm md:max-w-lg text-white/65 text-sm sm:text-base md:text-lg leading-relaxed mb-2"
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {t('home.hero.descriptionPrefix')}{' '}
          <strong className="text-white/85 font-semibold">{t('home.hero.descriptionStrong')}</strong>.
        </p>

        {/* Rose accent line */}
        <div className="flex items-center gap-3 mb-8 mt-4">
          <span className="w-6 h-px" style={{ background: 'rgba(232,54,93,0.45)' }} />
          <span className="text-[#E8365D] text-[10px] tracking-[0.22em] uppercase" style={{ fontFamily: "'Inter', sans-serif", opacity: 0.65 }}>SIG MOBI</span>
          <span className="w-6 h-px" style={{ background: 'rgba(232,54,93,0.45)' }} />
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <div className="w-px h-6 bg-gradient-to-b from-white/20 to-transparent" />
          <div className="w-1.5 h-1.5 rounded-full bg-[#E8365D]" style={{ opacity: 0.7 }} />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
